<script setup lang="ts">

import CircleLineJson from '@/assets/lotties/circle-line-explosion.json'
import { Vue3Lottie } from 'vue3-lottie'
import { computed, ref } from 'vue'

const props = defineProps<{
  show: boolean
  imageSrc?: string
  imageRotateAngle?: string
  count?: number
  loop?: number | boolean
}>()

const showEmojis = ref(false)
// Generate evenly distributed angles for a perfect circle
const numberOfEmojis = computed(() => props.count ?? 8)
const explosionAngles = computed(() => {
  return Array.from({ length: numberOfEmojis.value }, (_, i) =>
    `${(360 / numberOfEmojis.value) * i}deg`, // Generate unique angles for perfect circle
  )
})

const startEmojiExplosion = () => {
  if (!props.imageSrc) {
    return
  }
  setTimeout(() => {
    showEmojis.value = true
  }, 500)

  // Clean up after animation
  setTimeout(() => {
    showEmojis.value = false
  }, 3000)
}

</script>

<template>
  <Transition name="fade">
    <Vue3Lottie
      v-if="show"
      :animation-data="CircleLineJson"
      :auto-play="true"
      :loop="loop ?? false"
      :delay="500"
      height="100%"
      :speed="1"
      class="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 min-h-[128px] min-w-[128px] z-[1000]"
      @on-animation-loaded="startEmojiExplosion"
    />
  </Transition>

  <!-- Emoji explosion container -->
  <Transition name="fade">
    <div
      v-if="showEmojis && imageSrc"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px]"
    >
      <!-- Outer container for each rocket -->
      <div
        v-for="(angle, index) in explosionAngles"
        :key="index"
        :class="['absolute', showEmojis ? 'emoji-wrapper' : 'hidden']"
        :style="{
          '--angle': angle,
          '--distance': '70px',
        }"
      >
        <!-- Inner image for tilt correction -->
        <img
          :src="imageSrc"
          :class="[showEmojis ? 'emoji-particle' : 'hidden']"
          :style="{
            '--imgAngle': imageRotateAngle,
          }"
        >
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.emoji-particle {
  position: absolute;
  width: 28px; // Adjust emoji size if needed
  height: 28px;
  animation: explosion 2s linear forwards;
  opacity: 0;
  transform: scale(0.01) translateZ(1px);
  will-change: transform, opacity;
}

@keyframes explosion {
  0% {
    transform: translate(-50%, -50%) rotate(90deg) scale(0.01) translateZ(1px);
    opacity: 0;
  }
  20% {
    opacity: 0;
    transform: rotate(var(--imgAngle)) scale(0.01) translateZ(1px);
  }
  50% {
    transform: rotate(var(--imgAngle)) scale(1) translateZ(1px);
  }
  75% {
    opacity: 1;
  }
  100% {
    transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngle)) scale(0.5) translateZ(1px);
    opacity: 0;
  }
}

.emoji-wrapper {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 28px; // Adjust emoji size if needed
  height: 28px;
  transform-origin: center;
  transform: translate(-50%, -50%) rotate(var(--angle));
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>